import type { StatusTrackerStepData } from '@/components/common';
import { WeekReportStatus } from '@/api/generated/models/weekReportStatus';
import i18n from '@/i18n';
import { currentLocale, isoWeekToDate } from '@/utils/format';

export const statusConfig: Record<WeekReportStatus, { labelKey: string; bg: string; fg: string }> =
  {
    [WeekReportStatus.DRAFT]: {
      labelKey: 'weekReports.status.draft',
      bg: 'var(--color-bg-surface-2)',
      fg: 'var(--color-text-secondary)'
    },
    [WeekReportStatus.IN_REVIEW]: {
      labelKey: 'weekReports.status.inReview',
      bg: 'var(--color-bg-surface-2)',
      fg: 'var(--color-text-secondary)'
    },
    [WeekReportStatus.SUBMITTED]: {
      labelKey: 'weekReports.status.submitted',
      bg: 'var(--color-status-warning-soft)',
      fg: 'var(--color-status-warning)'
    },
    [WeekReportStatus.UNDER_REVIEW]: {
      labelKey: 'weekReports.status.underReview',
      bg: 'var(--color-status-warning-soft)',
      fg: 'var(--color-status-warning)'
    },
    [WeekReportStatus.APPROVED]: {
      labelKey: 'weekReports.status.approved',
      bg: 'var(--color-status-success-soft)',
      fg: 'var(--color-status-success)'
    },
    [WeekReportStatus.REJECTED]: {
      labelKey: 'weekReports.status.rejected',
      bg: 'var(--color-status-danger-soft)',
      fg: 'var(--color-status-danger)'
    },
    [WeekReportStatus.CORRECTED]: {
      labelKey: 'weekReports.status.corrected',
      bg: 'var(--color-status-warning-soft)',
      fg: 'var(--color-status-warning)'
    },
    [WeekReportStatus.RESUBMITTED]: {
      labelKey: 'weekReports.status.resubmitted',
      bg: 'var(--color-role-user-soft)',
      fg: 'var(--color-role-user)'
    }
  };

const shortDate = (d: Date) =>
  d.toLocaleDateString(currentLocale(), { day: '2-digit', month: '2-digit' });

/**
 * Koraci za StatusTracker (Figma node 4859:13767) — vidi offerDisplay.ts (buildOfferTrackerSteps)
 * za opću napomenu o principu. IN_REVIEW ("Interno odobreno") je OBAVEZAN korak nakon Nacrta
 * (isti obrazac kao OfferStatus.APPROVED) — prikazuje se kao zaseban, vidljiv korak, ne spaja se
 * više s Nacrtom. WeekReportStatus ima i petlju za ispravak (REJECTED → CORRECTED → RESUBMITTED →
 * natrag na pregled), koju Status Tracker (linearan po dizajnu) ne može doslovno prikazati — zato
 * se CORRECTED/RESUBMITTED mapiraju NATRAG na poziciju Predan/U pregledu (ispravak ne traži
 * ponovno interno odobrenje), umjesto da ostanu trajno prikazani kao iznimka (Exception je po
 * dizajnu privremen dok se problem ne razriješi, ne trajno stanje). Naziv koraka je uvijek naziv
 * STVARNOG trenutnog statusa (labelFor(status), ne kanonskog s pozicije) kad je korak "current",
 * da se poklapa s badgeom u headeru — a naziv kanonskog statusa (labelFor(s)) za done/upcoming
 * korake, jer za njih nema badgea s kojim bi se moglo pobrkati.
 */
export const buildWeekReportTrackerSteps = (status: WeekReportStatus): StatusTrackerStepData[] => {
  const t = i18n.t.bind(i18n);
  const labelFor = (s: WeekReportStatus) => t(statusConfig[s].labelKey);

  if (status === WeekReportStatus.REJECTED) {
    return [
      { key: 'draft', label: labelFor(WeekReportStatus.DRAFT), state: 'done' },
      { key: 'inReview', label: labelFor(WeekReportStatus.IN_REVIEW), state: 'done' },
      { key: 'submitted', label: labelFor(WeekReportStatus.SUBMITTED), state: 'done' },
      { key: 'exception', label: labelFor(WeekReportStatus.REJECTED), state: 'exception' }
    ];
  }

  const effectiveStatus =
    status === WeekReportStatus.CORRECTED
      ? WeekReportStatus.SUBMITTED
      : status === WeekReportStatus.RESUBMITTED
        ? WeekReportStatus.UNDER_REVIEW
        : status;

  const order = [
    WeekReportStatus.DRAFT,
    WeekReportStatus.IN_REVIEW,
    WeekReportStatus.SUBMITTED,
    WeekReportStatus.UNDER_REVIEW,
    WeekReportStatus.APPROVED
  ];
  const currentIndex = order.indexOf(effectiveStatus);

  return order.map((s, index) => {
    const state: StatusTrackerStepData['state'] =
      index < currentIndex ? 'done' : index > currentIndex ? 'upcoming' : 'current';
    return { key: s, label: state === 'current' ? labelFor(status) : labelFor(s), state };
  });
};

export const formatWeekLabel = (weekNumber?: number, year?: number) => {
  const start = isoWeekToDate(weekNumber, year);
  if (!start) return '—';
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  return i18n.t('weekReports.weekLabel', {
    week: weekNumber,
    range: `${shortDate(start)}–${shortDate(end)}`
  });
};

export const formatWeekPeriod = (weekNumber?: number, year?: number) => {
  const start = isoWeekToDate(weekNumber, year);
  if (!start) return '—';
  const end = new Date(start);
  end.setDate(start.getDate() + 6);
  const fmt = (d: Date) => d.toLocaleDateString(currentLocale());
  return `${fmt(start)} — ${fmt(end)}`;
};
